import React, { useEffect, useRef, useState } from "react";

// Apple-style scroll-driven reveal: element scales up, fades in and its corners
// tighten as it travels into the centre of the viewport.
export const ScrollScale = ({ children, className = "", from = 0.9 }) => {
  const ref = useRef(null);
  const [p, setP] = useState(0);

  useEffect(() => {
    let raf = null;
    const compute = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const start = vh * 0.95; // just entering from bottom
      const end = vh * 0.4;     // reaches sweet spot
      const prog = (start - rect.top) / (start - end);
      setP(Math.max(0, Math.min(1, prog)));
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { compute(); raf = null; });
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const scale = from + (1 - from) * p;
  const radius = 40 - 16 * p;
  const opacity = 0.55 + 0.45 * p;

  return (
    <div
      ref={ref}
      className={className}
      style={{
        transform: `scale(${scale})`,
        opacity,
        borderRadius: `${radius}px`,
        willChange: "transform, opacity",
      }}
    >
      {children}
    </div>
  );
};

// Subtle vertical parallax for decorative/media elements.
export const Parallax = ({ children, className = "", speed = 0.15 }) => {
  const ref = useRef(null);
  const [y, setY] = useState(0);
  useEffect(() => {
    let raf = null;
    const compute = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const centre = rect.top + rect.height / 2 - vh / 2;
      setY(-centre * speed);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { compute(); raf = null; });
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [speed]);
  return (
    <div ref={ref} className={className} style={{ transform: `translate3d(0, ${y}px, 0)`, willChange: "transform" }}>
      {children}
    </div>
  );
};
